import { useEffect, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Compass } from "@phosphor-icons/react";

/**
 * Lightweight 4-step guided tour overlay.
 *
 * Each step points to an existing element by data-testid (or CSS selector).
 * The overlay dims the page, highlights the target, and shows a tooltip.
 * Non-blocking: user can skip anytime.
 */

const TOUR_STEPS = [
  {
    id: "url-input",
    title: "1 · Drop any URL",
    body: "Paste an affiliate landing page. We scrape the product, benefits, and CTA in <2s.",
    testid: "url-input",
    placement: "bottom",
  },
  {
    id: "nav-links",
    title: "2 · Track affiliate links",
    body: "Save your affiliate URLs here and copy tracker links that count clicks + conversions.",
    testid: "nav-links",
    placement: "right",
  },
  {
    id: "nav-schedules",
    title: "3 · Automate campaigns",
    body: "Set-and-forget: pick a cadence (daily / weekly) and we auto-regenerate fresh ads.",
    testid: "nav-schedules",
    placement: "right",
  },
  {
    id: "nav-analytics",
    title: "4 · See what's working",
    body: "Live leaderboards rank variants by clicks, CVR, or revenue — you pick the metric.",
    testid: "nav-analytics",
    placement: "right",
  },
];

function TourTooltip({ step, index, total, onNext, onPrev, onSkip }) {
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    const el = document.querySelector(`[data-testid="${step.testid}"]`);
    if (!el) {
      setPos(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [step]);

  useEffect(() => {
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    // Retry once after a short delay for async-rendered targets
    const t = setTimeout(updatePos, 300);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
      clearTimeout(t);
    };
  }, [updatePos]);

  if (!pos) {
    // Target not found — show a centered fallback so tour still progresses
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
        data-testid={`tour-step-${index}`}
      >
        <div
          className="bg-[#0a0a0a] border border-[#ccff00] p-5 max-w-sm pointer-events-auto"
          style={{ boxShadow: "0 0 0 4px rgba(204,255,0,0.15)" }}
        >
          <TooltipBody
            step={step}
            index={index}
            total={total}
            onNext={onNext}
            onPrev={onPrev}
            onSkip={onSkip}
          />
        </div>
      </div>
    );
  }

  // Compute tooltip position
  const isRight = step.placement === "right";
  const tooltipLeft = isRight ? pos.left + pos.width + 16 : pos.left;
  const tooltipTop = isRight ? pos.top : pos.top + pos.height + 12;

  return (
    <>
      {/* Highlight ring around target */}
      <div
        className="fixed z-[9998] pointer-events-none transition-all duration-300"
        style={{
          top: pos.top - 6,
          left: pos.left - 6,
          width: pos.width + 12,
          height: pos.height + 12,
          border: "2px solid #ccff00",
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.72), 0 0 24px rgba(204,255,0,0.5)",
        }}
      />
      {/* Tooltip */}
      <div
        className="fixed z-[9999] w-80 bg-[#0a0a0a] border border-[#ccff00] p-5 pointer-events-auto"
        style={{
          top: Math.min(tooltipTop, window.innerHeight - 260),
          left: Math.min(Math.max(12, tooltipLeft), window.innerWidth - 340),
        }}
        data-testid={`tour-step-${index}`}
      >
        <TooltipBody
          step={step}
          index={index}
          total={total}
          onNext={onNext}
          onPrev={onPrev}
          onSkip={onSkip}
        />
      </div>
    </>
  );
}

function TooltipBody({ step, index, total, onNext, onPrev, onSkip }) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#ccff00]">
          {index + 1} / {total}
        </div>
        <button
          data-testid="tour-skip"
          onClick={onSkip}
          className="p-1 text-zinc-500 hover:text-white transition-colors"
          aria-label="skip tour"
        >
          <X size={14} />
        </button>
      </div>
      <div className="font-display text-lg font-bold text-white mb-2 leading-tight">
        {step.title}
      </div>
      <div className="text-[12px] text-zinc-400 leading-relaxed mb-4">
        {step.body}
      </div>
      <div className="flex items-center justify-between gap-2">
        <button
          data-testid="tour-prev"
          onClick={onPrev}
          disabled={index === 0}
          className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white disabled:opacity-30 flex items-center gap-1 transition-colors"
        >
          <ArrowLeft size={11} />
          back
        </button>
        <button
          data-testid="tour-next"
          onClick={onNext}
          className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold py-2 px-4 flex items-center gap-1.5 text-xs transition-colors"
        >
          {index === total - 1 ? "Finish" : "Next"}
          <ArrowRight size={11} weight="bold" />
        </button>
      </div>
    </>
  );
}

export default function TourOverlay({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;
  const total = TOUR_STEPS.length;
  const cur = TOUR_STEPS[step];

  const next = () => {
    if (step === total - 1) onClose();
    else setStep(step + 1);
  };
  const prev = () => setStep(Math.max(0, step - 1));

  return (
    <TourTooltip
      step={cur}
      index={step}
      total={total}
      onNext={next}
      onPrev={prev}
      onSkip={onClose}
    />
  );
}

/** Trigger a fresh tour from any page. */
export function TourLauncher({ onLaunch }) {
  return (
    <button
      data-testid="launch-tour-btn"
      onClick={onLaunch}
      className="fixed bottom-6 right-6 z-40 bg-[#0a0a0a] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-400 p-3 shadow-lg transition-all"
      title="Take the tour"
    >
      <Compass size={18} />
    </button>
  );
}
